<template>
  <v-card outlined class="mb-4">
    <v-card-text>
      <!-- Material balance overview -->
      <v-card outlined color="blue lighten-5" class="mb-4">
        <v-card-text>
          <div class="d-flex align-center mb-3">
            <h3 class="text-h6 mb-0">Balans materijala</h3>
            <v-tooltip right max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <v-icon color="primary" class="ml-2" small v-bind="attrs" v-on="on">
                  mdi-information
                </v-icon>
              </template>
              <span>
                <strong>Kalo</strong> je postotak materijala koji se otpisuje kao gubitak (npr. kosti, mast, voda koja ispari).
                Odabirom kala smanjujete količinu materijala koja mora biti iskorištena u proizvodima.
              </span>
            </v-tooltip>
          </div>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Materijal</th>
                  <th class="text-right">Dostupno</th>
                  <th class="text-center">Kalo</th>
                  <th class="text-right">Za korištenje</th>
                  <th class="text-right">Iskorišteno</th>
                  <th class="text-right">Preostalo</th>
                  <th class="text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="material in materialBalance" :key="material.id">
                  <td>{{ material.name }}</td>
                  <td class="text-right">
                    {{ material.originalAvailable.toFixed(2) }} {{ material.unit }}
                  </td>
                  <td class="text-center" style="min-width: 180px;">
                    <div class="mt-3 px-1">
                      <v-slider
                        :value="materialWastage[material.id] || 0"
                        @input="onWastageSliderChange(material.id, $event)"
                        min="0"
                        max="20"
                        step="0.1"
                        thumb-label="always"
                        color="orange darken-1"
                        track-color="grey lighten-2"
                        thumb-color="orange"
                        dense
                        hide-details
                      >
                        <template v-slot:prepend>
                          <span class="caption grey--text font-weight-bold">0%</span>
                        </template>
                        <template v-slot:append>
                          <span class="caption grey--text font-weight-bold">20%</span>
                        </template>
                        <template v-slot:thumb-label="{ value }">
                          {{ value.toFixed(1) }}%
                        </template>
                      </v-slider>
                      <div v-if="materialWastage[material.id] > 0" class="text-center mt-2 mb-1">
                        <v-chip small color="orange lighten-4" class="orange--text text--darken-2">
                          <v-icon x-small left color="orange darken-2">mdi-arrow-down</v-icon>
                          <strong>{{ material.wastageAmount.toFixed(3) }} {{ material.unit }}</strong>
                        </v-chip>
                      </div>
                      <div v-if="materialWastage[material.id] > 0" class="caption grey--text text-center" style="font-size: 10px; line-height: 1.2;">
                        Otpis: {{ material.wastagePercentage.toFixed(1) }}% od {{ material.originalAvailable.toFixed(2) }} {{ material.unit }}
                      </div>
                    </div>
                  </td>
                  <td class="text-right">
                    <strong>{{ material.available.toFixed(2) }} {{ material.unit }}</strong>
                  </td>
                  <td class="text-right">
                    {{ material.used.toFixed(2) }} {{ material.unit }}
                  </td>
                  <td class="text-right">
                    <span :class="material.remaining < 0 ? 'error--text font-weight-bold' : ''">
                      {{ material.remaining.toFixed(2) }} {{ material.unit }}
                    </span>
                  </td>
                  <td class="text-right">
                    <v-chip
                      small
                      :color="getBalanceColor(material)"
                      text-color="white"
                    >
                      {{ getBalanceStatus(material) }}
                    </v-chip>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <v-alert
            v-if="hasUnusedMaterials && !hasOverusedMaterials"
            type="error"
            dense
            outlined
            class="mt-3 mb-0"
          >
            <v-icon left small>mdi-alert</v-icon>
            <strong>Ne možete nastaviti!</strong> Imate neiskorištene materijale:
            <ul class="mt-2 mb-0">
              <li v-for="material in unusedMaterials" :key="material.id">
                <strong>{{ material.name }}</strong>: još {{ material.remaining.toFixed(2) }} {{ material.unit }} koje trebate iskoristiti
              </li>
            </ul>
            <div class="mt-2">
              <small>Dodajte nove proizvode koji će iskoristiti preostale materijale.</small>
            </div>
          </v-alert>

          <v-alert
            v-if="hasOverusedMaterials"
            type="error"
            dense
            outlined
            class="mt-3 mb-0"
          >
            <v-icon left small>mdi-alert-circle</v-icon>
            <strong>Ne možete nastaviti!</strong> Koristite više materijala nego što je dostupno:
            <ul class="mt-2 mb-0">
              <li v-for="material in overusedMaterials" :key="material.id">
                <strong>{{ material.name }}</strong>: koristite {{ Math.abs(material.remaining).toFixed(2) }} {{ material.unit }} više nego što je dostupno
              </li>
            </ul>
            <div class="mt-2">
              <small>Smanjite količine proizvoda ili dodajte više sirovine.</small>
            </div>
          </v-alert>

          <v-alert
            v-if="isBalanced"
            type="success"
            dense
            outlined
            class="mt-3 mb-0"
          >
            <v-icon left small>mdi-check-circle</v-icon>
            <strong>Perfektno!</strong> Svi materijali su potpuno iskorišteni.
          </v-alert>
        </v-card-text>
      </v-card>

      <!-- Products list -->
      <div class="d-flex align-center mb-3">
        <h3 class="text-h6">Proizvodi</h3>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          outlined
          @click="addProduct"
        >
          <v-icon left>mdi-plus</v-icon>
          Dodaj proizvod
        </v-btn>
      </div>

      <v-alert v-if="products.length === 0" type="info" outlined dense>
        Dodajte barem jedan proizvod koji želite proizvesti
      </v-alert>

      <!-- Product cards -->
      <v-card
        v-for="(product, productIndex) in products"
        :key="product.localId"
        outlined
        class="mb-4"
      >
        <v-card-title class="pb-2">
          <span class="subtitle-1">Proizvod {{ productIndex + 1 }}</span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            small
            color="error"
            @click="removeProduct(productIndex)"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="product.item"
                :items="availableProducts"
                item-text="name"
                item-value="id"
                return-object
                label="Proizvod"
                prepend-icon="mdi-food"
                outlined
                dense
                clearable
                @change="onProductChange(product)"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle>Šifra: {{ item.code }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="product.quantity"
                type="number"
                :label="`Količina (${product.item ? product.item.unit : 'kg'})`"
                outlined
                dense
                :suffix="product.item ? product.item.unit : 'kg'"
                :rules="[rules.required, rules.positive]"
                @input="emitUpdate"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="4">
              <v-select
                v-model="product.warehouse"
                :items="warehouses"
                item-text="name"
                item-value="id"
                return-object
                label="Odredišno skladište"
                prepend-icon="mdi-warehouse"
                outlined
                dense
                @change="emitUpdate"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle>{{ item.location }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-select>
            </v-col>
          </v-row>

          <v-divider class="my-3"></v-divider>

          <!-- Recipe for this product -->
          <h4 class="subtitle-2 mb-2">Receptura (koje sirovine se koriste)</h4>

          <v-alert v-if="!product.item" type="info" outlined dense>
            Najprije odaberite proizvod
          </v-alert>

          <div v-else>
            <v-card
              v-for="(ingredient, ingredientIndex) in product.recipe"
              :key="ingredientIndex"
              outlined
              class="mb-2"
            >
              <v-card-text class="py-2">
                <v-row align="center">
                  <v-col cols="12" md="5">
                    <v-select
                      v-model="ingredient.sourceItem"
                      :items="sourceItemsList"
                      item-text="name"
                      item-value="id"
                      return-object
                      label="Sirovina"
                      prepend-icon="mdi-package-variant"
                      outlined
                      dense
                      @change="emitUpdate"
                    >
                      <template v-slot:item="{ item }">
                        <v-list-item-content>
                          <v-list-item-title>{{ item.name }}</v-list-item-title>
                          <v-list-item-subtitle>
                            Dostupno: {{ getAvailableQuantity(item.id).toFixed(2) }} {{ item.unit }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                    </v-select>
                  </v-col>

                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model.number="ingredient.quantityPerUnit"
                      type="number"
                      :label="`Po ${product.item ? product.item.unit : 'jedinici'}`"
                      outlined
                      dense
                      :suffix="ingredient.sourceItem ? ingredient.sourceItem.unit : 'kg'"
                      hint="Koliko sirovine za 1 jedinicu proizvoda"
                      persistent-hint
                      @input="emitUpdate"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" md="3">
                    <v-text-field
                      :value="(Number(ingredient.quantityPerUnit) * Number(product.quantity)).toFixed(2)"
                      label="Ukupno potrebno"
                      outlined
                      dense
                      readonly
                      :suffix="ingredient.sourceItem ? ingredient.sourceItem.unit : 'kg'"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" md="1">
                    <v-btn
                      icon
                      small
                      color="error"
                      @click="removeIngredient(product, ingredientIndex)"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <v-btn
              color="primary"
              outlined
              small
              block
              @click="addIngredient(product)"
              class="mb-2"
            >
              <v-icon left small>mdi-plus</v-icon>
              Dodaj sirovinu
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'MultipleProductsDefinition',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    sourceItems: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      products: [],
      nextLocalId: 1,
      materialWastage: {}, // { materialId: wastagePercentage (0-20 with 0.1 precision) }

      // Mock warehouses
      warehouses: [
        { id: 1, name: 'Glavno skladište', location: 'Zagreb' },
        { id: 2, name: 'Skladište sirovine', location: 'Zagreb' },
        { id: 3, name: 'Skladište gotovih proizvoda', location: 'Split' },
        { id: 4, name: 'Hladnjača', location: 'Rijeka' }
      ],

      // Mock products
      availableProducts: [
        { id: 101, name: 'Ćevapčići', code: 'CEV-001', unit: 'kg', recipeYield: 50 },
        { id: 102, name: 'Pljeskavice', code: 'PLJ-001', unit: 'kg', recipeYield: 10 },
        { id: 103, name: 'Hamburger', code: 'HAM-001', unit: 'kg', recipeYield: 15 },
        { id: 104, name: 'Kobasice', code: 'KOB-001', unit: 'kg', recipeYield: 20 },
        { id: 105, name: 'Mesne okruglice', code: 'OKR-001', unit: 'kg', recipeYield: 40 },
        { id: 201, name: 'Junetina kosti', code: 'JUN-KOSTI', unit: 'kg', recipeYield: 1 },
        { id: 202, name: 'Junetina 2. kategorija', code: 'JUN-2KAT', unit: 'kg', recipeYield: 1 },
        { id: 203, name: 'Svinjetina kosti', code: 'SVI-KOSTI', unit: 'kg', recipeYield: 1 },
        { id: 204, name: 'Svinjetina mast', code: 'SVI-MAST', unit: 'kg', recipeYield: 1 }
      ],

      rules: {
        required: v => (v != null && v !== '') || 'Obavezno polje',
        positive: v => v > 0 || 'Količina mora biti veća od 0'
      }
    }
  },
  computed: {
    sourceItemsList () {
      return this.sourceItems
        .filter(si => si && si.item)
        .map(si => ({
          ...si.item,
          quantity: si.quantity
        }))
    },
    materialBalance () {
      const balance = {}

      // Initialize with source items
      this.sourceItems.forEach(sourceItem => {
        // Skip if item is not defined
        if (!sourceItem || !sourceItem.item) return

        const originalQty = Number(sourceItem.quantity) || 0
        const materialId = sourceItem.item.id

        // Get wastage percentage (can be 0-20 with 0.1 precision from slider)
        const wastagePercentage = this.materialWastage[materialId] || 0
        const wastageAmount = (originalQty * wastagePercentage) / 100
        const availableAfterWastage = originalQty - wastageAmount

        balance[materialId] = {
          id: materialId,
          name: sourceItem.item.name,
          unit: sourceItem.item.unit,
          originalAvailable: originalQty,
          wastagePercentage: wastagePercentage,
          wastageAmount: wastageAmount,
          available: availableAfterWastage,
          used: 0,
          remaining: availableAfterWastage
        }
      })

      // Calculate used quantities from all products
      this.products.forEach(product => {
        const productQty = Number(product.quantity) || 0
        if (product.item && productQty > 0) {
          product.recipe.forEach(ingredient => {
            const qtyPerUnit = Number(ingredient.quantityPerUnit) || 0
            if (ingredient.sourceItem && qtyPerUnit > 0) {
              const materialId = ingredient.sourceItem.id
              if (balance[materialId]) {
                const totalUsed = qtyPerUnit * productQty
                balance[materialId].used += totalUsed
                balance[materialId].remaining = balance[materialId].available - balance[materialId].used
              }
            }
          })
        }
      })

      return Object.values(balance)
    },
    hasUnusedMaterials () {
      return this.materialBalance.some(m => m.remaining > 0.01) // Small tolerance for rounding
    },
    hasOverusedMaterials () {
      return this.materialBalance.some(m => m.remaining < -0.01)
    },
    isBalanced () {
      // All materials should be used (remaining close to 0) or explicitly handled
      return !this.hasOverusedMaterials && !this.hasUnusedMaterials
    },
    unusedMaterials () {
      return this.materialBalance.filter(m => m.remaining > 0.01)
    },
    overusedMaterials () {
      return this.materialBalance.filter(m => m.remaining < -0.01)
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (newVal) {
        if (newVal && newVal.length > 0) {
          this.products = newVal
        }
      }
    },
    sourceItems: {
      immediate: true,
      handler (newVal) {
        // Initialize wastage to 0% for all materials
        if (newVal && newVal.length > 0) {
          newVal.forEach(sourceItem => {
            if (sourceItem && sourceItem.item) {
              const materialId = sourceItem.item.id
              if (this.materialWastage[materialId] === undefined) {
                this.$set(this.materialWastage, materialId, 0)
              }
            }
          })
        }
      }
    }
  },
  methods: {
    addProduct () {
      this.products.push({
        localId: this.nextLocalId++,
        item: null,
        quantity: 0,
        warehouse: null,
        recipe: []
      })
      this.emitUpdate()
    },
    removeProduct (index) {
      this.products.splice(index, 1)
      this.emitUpdate()
    },
    onProductChange (product) {
      if (product.item && product.recipe.length === 0) {
        this.addIngredient(product)
      }
      this.emitUpdate()
    },
    addIngredient (product) {
      product.recipe.push({
        sourceItem: null,
        quantityPerUnit: 0
      })
      this.emitUpdate()
    },
    removeIngredient (product, index) {
      product.recipe.splice(index, 1)
      this.emitUpdate()
    },
    getAvailableQuantity (sourceItemId) {
      const material = this.materialBalance.find(m => m.id === sourceItemId)
      return material ? material.remaining : 0
    },
    getBalanceColor (material) {
      if (material.remaining < -0.01) return 'error'
      if (material.remaining > 0.01) return 'warning'
      return 'success'
    },
    getBalanceStatus (material) {
      if (material.remaining < -0.01) return 'Prekoračeno'
      if (material.remaining > 0.01) return 'Neiskorišteno'
      return 'Balansirano'
    },
    onWastageSliderChange (materialId, percentage) {
      // Update the wastage percentage from slider (0-20 with 0.1 precision)
      const clampedPercentage = Math.max(0, Math.min(20, Number(percentage) || 0))
      this.$set(this.materialWastage, materialId, clampedPercentage)

      // Trigger re-computation of balance and emit update
      this.emitUpdate()
    },
    emitUpdate () {
      this.$emit('input', this.products)
      this.$emit('balance-status', {
        isBalanced: this.isBalanced,
        hasUnused: this.hasUnusedMaterials,
        hasOverused: this.hasOverusedMaterials,
        materialBalance: this.materialBalance,
        materialWastage: this.materialWastage
      })
    }
  }
}
</script>

<style scoped>
.v-card {
  transition: all 0.3s;
}

.v-alert ul {
  padding-left: 20px;
}

.v-alert li {
  margin: 4px 0;
}

/* Slider styling */
.v-slider >>> .v-slider__thumb-label {
  font-size: 12px;
  font-weight: bold;
}
</style>
